/* eslint-disable */
// Universal Global Search — New DNA overlay shell (Phase C). createPortal to body
// (Conv #126), scoped .gsRoot tokens (Conv #68). Full-width field + esc cap, meta
// line (live active portal + "works from any portal"), scope chips (All + 7
// groups), grouped results with top/bottom scroll fades, footer legend + live
// count. Consumes the portable useGlobalSearchSources; the matcher (searchAll)
// runs per keystroke over the memoized sources. ↑↓ across groups, Enter opens,
// Esc closes, click opens, chips filter. ⌘K open/close stays on App.js:434.
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './globalSearch.module.css';
import { GlobalSearchRow } from './GlobalSearchRow';
import { useGlobalSearchSources } from './useGlobalSearchSources';
import { searchAll, GROUP_META } from './globalSearchData';

const SearchIco = () => <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2"><circle cx="11" cy="11" r="7.5" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>;
const Reach = () => <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2"><path d="M20 6 9 17l-5-5" /></svg>;
const CHIPS = [{ k: 'all', label: 'All' }, ...GROUP_META];

export function GlobalSearchV2({ globalSearch, setGlobalSearch, onClose, onSelect, fields, role, activePortalLabel }) {
  const q = (globalSearch || '').trim();
  const [CAP,setCap] = useState(6);
  useEffect(()=>setCap(6),[q]);
  const sources = useGlobalSearchSources({ q, cap: CAP, fields, role });
  const [scope, setScope] = useState('all');
  const [activeIdx, setActiveIdx] = useState(0);
  const [stagger, setStagger] = useState(true);
  const [renderKey, setRenderKey] = useState(0);

  // One match pass per keystroke over the (memoized) sources — chip counts + view.
  const allGroups = useMemo(() => searchAll(sources, q, 'all'), [sources, q]);
  const view = scope === 'all' ? allGroups : allGroups.filter(g => g.k === scope);

  const flat = [];
  const rendered = view.map((g) => {
    const shown = g.rows.slice(0, CAP).map(r => ({ ...r, k: g.k }));
    const withIdx = shown.map(r => ({ r, i: flat.length + shown.indexOf(r) }));
    shown.forEach(r => flat.push(r));
    return { ...g, withIdx };
  });
  const active = Math.min(activeIdx, Math.max(0, flat.length - 1));

  const flatRef = useRef([]); flatRef.current = flat;
  const activeRef = useRef(0); activeRef.current = active;
  const activeRowRef = useRef(null);
  const onSelectRef = useRef(onSelect); onSelectRef.current = onSelect;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, Math.max(0, flatRef.current.length - 1))); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
      else if (e.key === 'Enter') { const d = flatRef.current[activeRef.current]; if (d) onSelectRef.current(d); }
      else if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { activeRowRef.current?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const pickScope = (k) => { setScope(k); setActiveIdx(0); setStagger(true); setRenderKey(x => x + 1); };
  const onType = (e) => { setGlobalSearch(e.target.value); setActiveIdx(0); setStagger(false); };

  let gi = 0;
  const body = (
    <div className={styles.gsRoot}>
      <div className={styles.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className={styles.gsModal} role="dialog" aria-label="Global search">
          <div className={styles.gsHead}>
            <div className={styles.searchField}>
              <SearchIco />
              <input autoFocus value={globalSearch} onChange={onType} placeholder="Search everything — items, designers, brands, portals, pages…" autoComplete="off" spellCheck="false" />
              <span className={styles.kbdCap} onClick={onClose}>esc</span>
            </div>
            <div className={styles.metaLine}>
              <span className={styles.ctxChip}><span className={styles.dot} />Searching from&nbsp;<b>{activePortalLabel || 'Operations Hub'}</b></span>
              <span className={styles.reach}><Reach />Works from any portal</span>
            </div>
          </div>

          <div className={styles.scopeRow}>
            {CHIPS.map(c => {
              const cnt = c.k === 'all' ? null : (allGroups.find(g => g.k === c.k)?.total ?? 0);
              return (
                <button key={c.k} className={`${styles.chip} ${scope === c.k ? styles.on : ''}`} onClick={() => pickScope(c.k)}>
                  {c.ico && <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" dangerouslySetInnerHTML={{ __html: c.ico }} />}
                  {c.label}{cnt != null && <span className={styles.cnt}>{cnt}</span>}
                </button>
              );
            })}
          </div>

          <div className={styles.resWrap}>
            {view.some(g=>g.total>Math.min(g.rows.length,CAP))&&<button type="button" className={styles.chip} disabled={sources.loading} onClick={()=>setCap(c=>c+12)}>Show more matches</button>}
            <div key={renderKey} className={`${styles.results} ${stagger ? styles.stagger : ''}`}>
              {!q && <div className={styles.hintLine}><svg viewBox="0 0 24 24" fill="none" strokeWidth="2"><path d="M12 3v2M12 19v2M3 12h2M19 12h2M6 6l1.5 1.5M18 18l-1.5-1.5M6 18l1.5-1.5M18 6l-1.5 1.5" /></svg>Suggested — start typing to search across everything</div>}
              {sources.loading && <div className={styles.hintLine} role="status">Searching accessible records…</div>}
              {sources.error && <div className={styles.hintLine} role="status">Some sources could not be searched. Try again.</div>}
              {flat.length === 0 && q && !sources.loading ? (
                <div className={styles.emptyBlk}>
                  <svg className={styles.eIco} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="7.5" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>
                  <h4>No matches for “{q}”</h4><p>Try a model, SKU, designer, brand, portal or page name.</p>
                </div>
              ) : rendered.map((g) => (
                <div className={styles.grp} key={g.k}>
                  <div className={styles.grpHead} style={{ '--gi': gi++ }}>
                    <svg className={styles.gIco} viewBox="0 0 24 24" fill="none" strokeWidth="2" dangerouslySetInnerHTML={{ __html: g.ico }} />
                    <span className={styles.gLbl}>{g.label}</span><span className={styles.gCnt}>{g.total}</span><span className={styles.gLine} />
                  </div>
                  {g.withIdx.map(({ r, i }) => (
                    <div key={i} ref={i === active ? activeRowRef : null} className={`${styles.row} ${i === active ? styles.active : ''}`} style={{ '--i': Math.min(i, 14) }}
                      onMouseEnter={() => setActiveIdx(i)} onClick={() => onSelect(r)}>
                      <GlobalSearchRow d={r} q={q} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.fadeTop} /><div className={styles.fadeBot} />
          </div>

          <div className={styles.gsFoot}>
            <div className={styles.legend} data-hide-on-touch>
              <span className={styles.leg}><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
              <span className={styles.leg}><kbd>↵</kbd> open</span>
              <span className={styles.leg}><kbd>esc</kbd> close</span>
            </div>
            <div className={styles.footRight}><b>{flat.length}</b> results</div>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(body, document.body);
}
